import * as React from "react";
import { Tooltip } from "@mui/material";

/**
 * The one hover tooltip these screens are allowed to use.
 *
 * Almost everything that used to be a tooltip here is now helper text under the field, the way
 * step 1 does it. What is left is the handful of cases where there is genuinely nowhere to put a
 * label — an icon-only button, a chip that is already at its full width, a truncated cell — and
 * those all come through here so there is exactly one surface, one delay and one set of collision
 * rules to reason about. The look lives in `MuiTooltip` in the theme, not in this file.
 *
 * The rule this component exists to enforce: **a tooltip that has nothing to say does not open.**
 * The admin queue used to hand MUI a template it had already filled with placeholders —
 * `Submitted — · confirmed —` — and MUI has no way to know that is not a sentence, so it drew it.
 * Here an empty, whitespace-only or all-placeholder title means the child is returned untouched:
 * no popper, no listeners, no cursor change, nothing to hover.
 */

/** What `formatDateTime`, `formatMoney` and friends return when they were handed nothing. */
const PLACEHOLDERS = new Set(["—", "-", "–", "n/a", "N/A", "null", "undefined"]);

function isEmptyTitle(title: React.ReactNode): boolean {
	if (title === null || title === undefined || title === false) return true;
	if (typeof title === "string") return title.trim().length === 0;
	return false;
}

export interface HelpTooltipProps {
	/**
	 * Short — two lines at 320px is the ceiling. Anything longer is helper text and belongs under
	 * the field. Nullish or blank and nothing renders.
	 */
	title: React.ReactNode;
	/** A single element that can take a ref. Chips, buttons and typography all can. */
	children: React.ReactElement;
	placement?: React.ComponentProps<typeof Tooltip>["placement"];
	/**
	 * Icon-only controls need their tooltip to be the accessible name too, not just a hover
	 * affordance. On by default for that reason; turn it off when the child already has its own
	 * `aria-label` and the tooltip is only adding colour.
	 */
	describeChild?: boolean;
}

export function HelpTooltip({ title, children, placement, describeChild = true }: HelpTooltipProps): React.JSX.Element {
	if (isEmptyTitle(title)) return children;

	return (
		<Tooltip title={title} placement={placement} describeChild={describeChild}>
			{children}
		</Tooltip>
	);
}

/**
 * Builds the `label value · label value` line the queue and the detail screens hover, dropping
 * every pair whose value never arrived.
 *
 * This is the other half of the `Submitted — · confirmed —` fix. Interpolating a formatter's
 * placeholder into a template is easy to write and impossible to spot in review, so the join is
 * done here instead: a pair is kept only if its value is a non-blank string that is not one of
 * the placeholders a formatter emits, and if nothing survives the answer is `null` — which
 * `HelpTooltip` then declines to render at all.
 *
 * ```ts
 * <HelpTooltip title={factLine([["Submitted", formatDateTime(row.submittedAt)],
 *                              ["Confirmed", formatDateTime(row.confirmedAt)]])}>
 * ```
 */
export function factLine(facts: [label: string, value: string | null | undefined][]): string | null {
	const kept = facts
		.filter(([, value]) => {
			if (value === null || value === undefined) return false;
			const trimmed = value.trim();
			return trimmed.length > 0 && !PLACEHOLDERS.has(trimmed);
		})
		.map(([label, value]) => `${label} ${(value as string).trim()}`);

	return kept.length > 0 ? kept.join(" · ") : null;
}
